import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { User } from './icons';

import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';
import UserAutoComplete from './UserAutoComplete';
import MultipleFileUpload from './MultipleFileUpload';
import ParticipantesPicker from './ParticipantesPicker';

export default function CreateTicketModal({ isOpen, onClose, onTicketCreated, mode = 'self' }) {
    const isAdmin = mode === 'other';
    const { user } = useAuth();
    const { notifyTicketCreated, alertSuccess, alertError } = useNotificationContext();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        descripcion: '',
        nombreSolicitante: '',
        emailSolicitante: '',
        telefonoSolicitante: '',
        empresa: '',
        ubicacion: '',
        subcategoriaId: null
    });

    const [loading, setLoading] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [participantes, setParticipantes] = useState([]);
    const esStaff = ['admin', 'soporte'].includes(user?.role?.toLowerCase());
    const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
    const [usuarioSeleccionadoId, setUsuarioSeleccionadoId] = useState('');
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState('');
    const [cargandoCategorias, setCargandoCategorias] = useState(true);

    // Estado para archivos (lógica manual similar a las páginas originales)
    const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    // Reset del formulario al abrir
    useEffect(() => {
        if (isOpen) {
            // Resetear estados
            setArchivosSeleccionados([]);
            setCategoriaSeleccionada('');
            setSubcategoriaSeleccionada('');
            setSubcategorias([]);
            setUsuarioSeleccionadoId('');
            setParticipantes([]);

            // Resetear data inicial
            const initialData = {
                descripcion: '',
                nombreSolicitante: '',
                emailSolicitante: '',
                telefonoSolicitante: '',
                empresa: '',
                ubicacion: '',
                subcategoriaId: null
            };

            if (!isAdmin && user) {
                // Modo usuario normal: Prellenar con datos del usuario actual
                initialData.nombreSolicitante = user.nombre && user.apellido ? `${user.nombre} ${user.apellido}` : user.nombre || user.username || '';
                initialData.emailSolicitante = user.email || '';
                initialData.telefonoSolicitante = user.telefono || '';
                initialData.empresa = user.empresa || '';
                initialData.ubicacion = user.ubicacion || '';
            }

            setFormData(initialData);
        }
    }, [isOpen, isAdmin, user]);

    // Cargar categorías al montar (o al abrir)
    useEffect(() => {
        if (isOpen) {
            const cargarCategorias = async () => {
                try {
                    const response = await api.get('/categoriasticket');
                    setCategorias(response.data);
                } catch (error) {
                    console.error('Error al cargar categorías:', error);
                } finally {
                    setCargandoCategorias(false);
                }
            };
            cargarCategorias();
        }
    }, [isOpen]);

    // Cargar usuarios (nómina completa, con estado de cuenta) solo si es admin y está abierto
    useEffect(() => {
        if (isOpen && isAdmin) {
            const cargarUsuarios = async () => {
                setCargandoUsuarios(true);
                try {
                    const response = await api.get('/usuarios');
                    setUsuarios(response.data);
                } catch (error) {
                    console.error('Error al cargar usuarios:', error);
                } finally {
                    setCargandoUsuarios(false);
                }
            };
            cargarUsuarios();
        }
    }, [isOpen, isAdmin]);

    // Cargar subcategorías
    useEffect(() => {
        const cargarSubcategorias = async () => {
            if (categoriaSeleccionada) {
                try {
                    const response = await api.get(`/categoriasticket/${categoriaSeleccionada}/subcategorias`);
                    setSubcategorias(response.data);
                    setSubcategoriaSeleccionada('');
                    setFormData(prev => ({ ...prev, subcategoriaId: null }));
                } catch (error) {
                    console.error('Error al cargar subcategorías:', error);
                    setSubcategorias([]);
                }
            } else {
                setSubcategorias([]);
            }
        };
        cargarSubcategorias();
    }, [categoriaSeleccionada]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUsuarioChange = (usuarioId) => {
        if (!isAdmin) return;
        setUsuarioSeleccionadoId(usuarioId);
        const usuario = usuarios.find(u => u.id === parseInt(usuarioId));

        if (usuario) {
            const nombreCompleto = [usuario.nombre, usuario.apellido]
                .filter(Boolean)
                .join(' ') || usuario.username || '';

            setFormData(prev => ({
                ...prev,
                nombreSolicitante: nombreCompleto,
                emailSolicitante: usuario.email || usuario.username || '',
                telefonoSolicitante: usuario.telefono || usuario.telefonoCorporativo || '',
                empresa: usuario.empresa || '',
                ubicacion: usuario.ubicacion || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                nombreSolicitante: '',
                emailSolicitante: '',
                telefonoSolicitante: '',
                empresa: '',
                ubicacion: ''
            }));
        }
    };

    // Manejo de archivos (Drag & Drop + Input)
    const handleMultipleFilesChange = (e) => {
        const newFiles = Array.from(e.target.files || []);
        procesarArchivos(newFiles);
        e.target.value = ''; // Reset input
    };

    const procesarArchivos = (files) => {
        if (files.length === 0) return;
        const validFiles = [];

        files.forEach(file => {
            const allowedTypes = [
                'image/jpeg',
                'image/png',
                'image/gif',
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/plain'
            ];
            if (!allowedTypes.includes(file.type)) {
                alertError(`Tipo no permitido: ${file.name}`);
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alertError(`Archivo muy grande: ${file.name}`);
                return;
            }
            validFiles.push(file);
        });

        if (validFiles.length > 0) {
            if (archivosSeleccionados.length + validFiles.length > 5) {
                alertError('Máximo 5 archivos permitidos.');
                return;
            }
            setArchivosSeleccionados(prev => [...prev, ...validFiles]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        procesarArchivos(files);
    };

    const removeArchivo = (index) => {
        setArchivosSeleccionados(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!formData.descripcion.trim() || !categoriaSeleccionada || !subcategoriaSeleccionada) {
            alertError('Complete los campos obligatorios.');
            setLoading(false);
            return;
        }

        try {
            let evidenciaUrl = null;

            // Subir primera evidencia si existe (como en original)
            if (archivosSeleccionados.length > 0) {
                const primerArchivo = archivosSeleccionados[0];
                const formDataFile = new FormData();
                formDataFile.append('file', primerArchivo);

                const uploadResponse = await api.post('/tickets/upload-evidence', formDataFile, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                evidenciaUrl = uploadResponse.data.url;
            }

            const ticketData = { ...formData, evidencia: evidenciaUrl, ...(esStaff && participantes.length ? { participantes: participantes.map(({ email, nombre, tipo }) => ({ email, nombre, tipo })) } : {}) };

            // Endpoint y lógica difieren ligeramente entre Admin y User según archivos originales
            // Admin usa /tickets siempre. User usa /tickets/create-authenticated si hay user.
            // Pero incluso CreateTicketAdmin usa /tickets.
            // Unificaremos usando la lógica de CreateTicket pero permitiendo override de datos si es admin.

            let endpoint = '/tickets';
            if (!isAdmin && user) {
                endpoint = '/tickets/create-authenticated';
            }
            // Nota: Si es admin creando para otro, usa /tickets y manda los datos del solicitante en el body.
            // Si es usuario creando para sí mismo, usa create-authenticated que ignora body solicitante y usa token (usualmente),
            // PERO CreateTicket.jsx manda ticketData COMPLETO de todas formas.

            const response = await api.post(endpoint, ticketData);
            notifyTicketCreated(response.data);

            const newTicketId = response.data.id;

            // Subir archivos extra
            if (archivosSeleccionados.length > 1) {
                const archivosAdicionales = archivosSeleccionados.slice(1);
                const formDataArchivos = new FormData();
                archivosAdicionales.forEach(a => formDataArchivos.append('archivos', a));

                try {
                    await api.post(`/tickets/${newTicketId}/archivos`, formDataArchivos, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } catch (err) {
                    console.error('Error subiendo archivos extra', err);
                }
            }

            alertSuccess('Ticket creado correctamente');

            if (onTicketCreated) onTicketCreated();

            // Redirigir al detalle del ticket y cerrar
            onClose();
            navigate(`/tickets/${newTicketId}`);

        } catch (error) {
            console.error('Error creando ticket:', error);
            alertError(error.response?.data?.message || 'Error al crear ticket');
        } finally {
            setLoading(false);
        }
    };


    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl mx-4 my-8 relative flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                        {isAdmin ? 'Crear Ticket para Usuario' : 'Crear Ticket para Mí'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Sección Usuario (Solo Admin) ó sólo vista para User */}
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">Información del Solicitante</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {isAdmin && (
                                    <div className="md:col-span-2">
                                        <UserAutoComplete
                                            value={usuarioSeleccionadoId}
                                            onChange={handleUsuarioChange}
                                            usuarios={usuarios}
                                            placeholder={cargandoUsuarios ? 'Cargando usuarios...' : 'Buscar usuario por nombre, departamento o email...'}
                                            label={
                                                <span>
                                                    <User className="w-4 h-4 inline mr-1" />
                                                    Seleccionar Usuario
                                                </span>
                                            }
                                            required
                                        />
                                    </div>
                                )}

                                {/* Campos de solo lectura (rellenados auto) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre</label>
                                    <input type="text" value={formData.nombreSolicitante} disabled className="w-full mt-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg cursor-not-allowed dark:text-gray-300 border border-gray-200 dark:border-gray-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                    <input type="text" value={formData.emailSolicitante} disabled className="w-full mt-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg cursor-not-allowed dark:text-gray-300 border border-gray-200 dark:border-gray-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Empresa</label>
                                    <input type="text" value={formData.empresa} disabled className="w-full mt-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg cursor-not-allowed dark:text-gray-300 border border-gray-200 dark:border-gray-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ubicación</label>
                                    <input type="text" value={formData.ubicacion} disabled className="w-full mt-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg cursor-not-allowed dark:text-gray-300 border border-gray-200 dark:border-gray-500" />
                                </div>
                            </div>
                        </div>

                        {/* Sección Ticket */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría *</label>
                                <select
                                    value={categoriaSeleccionada}
                                    onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                    required
                                >
                                    <option value="">Seleccione...</option>
                                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subcategoría *</label>
                                <select
                                    value={subcategoriaSeleccionada}
                                    onChange={(e) => {
                                        setSubcategoriaSeleccionada(e.target.value);
                                        setFormData(prev => ({ ...prev, subcategoriaId: parseInt(e.target.value) }));
                                    }}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                    required
                                    disabled={!categoriaSeleccionada}
                                >
                                    <option value="">Seleccione...</option>
                                    {subcategorias.map(sc => <option key={sc.id} value={sc.id}>{sc.nombre}</option>)}
                                </select>
                            </div>
                            {esStaff && (
                                <div className="md:col-span-2">
                                    <ParticipantesPicker
                                        titulo="Participantes adicionales"
                                        value={participantes}
                                        canEdit
                                        onAdd={(p) => setParticipantes((prev) => prev.some((x) => x.email === p.email) ? prev : [...prev, p])}
                                        onRemove={(p) => setParticipantes((prev) => prev.filter((x) => x.email !== p.email))}
                                    />
                                </div>
                            )}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción *</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
                                    required
                                />
                            </div>
                        </div>

                        {/* Evidencia */}
                        <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                                }`}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        >
                            <p className="text-gray-600 dark:text-gray-300 mb-2">Arrastra archivos aquí o haz clic para seleccionar (Máx 5, 10MB)</p>
                            <input
                                type="file"
                                multiple
                                onChange={handleMultipleFilesChange}
                                className="hidden"
                                id="modal-file-input"
                                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                            />
                            <label htmlFor="modal-file-input" className="cursor-pointer text-blue-600 hover:text-blue-500 font-medium">Seleccionar archivos</label>

                            {archivosSeleccionados.length > 0 && (
                                <div className="mt-4 grid grid-cols-2 gap-2 text-left">
                                    {archivosSeleccionados.map((file, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm bg-gray-100 dark:bg-gray-700 p-2 rounded">
                                            <span className="truncate max-w-[80%] dark:text-white">{file.name}</span>
                                            <button type="button" onClick={() => removeArchivo(i)} className="text-red-500 hover:text-red-700">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">Cancelar</button>
                            <button type="submit" disabled={loading} className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                                {loading ? 'Creando...' : 'Crear Ticket'}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}
